import { Component } from '@angular/core';
import { HttpService } from 'src/app/http.service';

// Panel approval screen for agent commission-wallet withdrawal requests.
// Agents submit requests from the front app (amount reserved on submit); admins
// approve here by choosing a company bank to pay FROM (or marking it a cash
// payout), or reject (which refunds the agent's reserved commission).
@Component({
  selector: 'app-agent-withdraw',
  templateUrl: './agent-withdraw.component.html',
  styleUrls: ['./agent-withdraw.component.scss']
})
export class AgentWithdrawComponent {

  public requests: any = [];
  public banks: any = [];
  public status: number = 2;
  public is_loading: boolean = false;
  public allow_edit_balance: boolean = false;
  public trim_step: number = 1;

  constructor(private http: HttpService) {
    this.init();
    this.getBanks();
  }

  // Resolve the edit/trim settings BEFORE loading rows so the auto-trim default
  // (whole-number payout) can be applied as the list renders.
  private async init() {
    await this.getSetting();
    await this.getRequests();
  }

  public async getSetting() {
    let rtn: any = await this.http.post("/admin/company/setting");
    const s = rtn && rtn.setting ? rtn.setting : {};
    // Dedicated agent gate takes precedence; when it isn't configured, fall back to
    // the shared player-withdraw edit gate (mirrors the API resolution).
    if (s.allow_edit_agent_withdraw_balance === true) {
      this.allow_edit_balance = true;
    } else if (s.allow_edit_agent_withdraw_balance === false) {
      this.allow_edit_balance = false;
    } else {
      this.allow_edit_balance = s.allow_edit_withdraw_balance === true;
    }
    let step = parseFloat(s.withdraw_trim_step);
    this.trim_step = (isNaN(step) || step <= 0) ? 1 : step;
  }

  public async getBanks() {
    let rtn: any = await this.http.post("/admin/bank/get");
    this.banks = rtn.banks;
  }

  public async getRequests(status: number = 2): Promise<void> {
    this.is_loading = true;
    this.status = status;
    let rtn: any = await this.http.post("/admin/agent-withdraw/get", { status: this.status });
    this.requests = rtn || [];
    // Seed the per-row admin action state. Default a pending row to Approved and
    // carry over its requested payout type so the UI shows the right controls.
    // Preserve the agent's originally requested amount as the editable ceiling.
    this.requests.forEach((r: any) => {
      r.action_status = 1;
      r.payout_type = r.payout_type || 'bank';
      r.max_amount = parseFloat(r.amount);
      // Default the payout to a whole number (drop the decimal) when editing is
      // enabled, so the admin never has to pay cents unless they deliberately
      // type them back. Only trims when the requested amount actually has a
      // fractional part; the trimmed difference is refunded on approval.
      if (this.allow_edit_balance && !isNaN(r.max_amount) && r.max_amount % 1 !== 0) {
        this.trimAmount(r);
      }
    });
    this.is_loading = false;
  }

  // Trim the editable payout down to the nearest LOWER multiple of the tenant's
  // configured trim step, always landing STRICTLY BELOW the current value, e.g.
  // with step 1: 69.86 -> 69, 70.00 -> 69; with step 5: 79.30 -> 75, 80 -> 75.
  // Used to drop the decimal so the agent payout is a whole number.
  trimAmount(request: any) {
    let v = parseFloat(request.amount);
    if (isNaN(v) || v <= 0) { request.amount = 0; return; }
    let s = parseFloat(this.trim_step as any);
    if (isNaN(s) || s <= 0) { s = 1; }
    let result = Math.floor(v / s) * s;
    if (result >= v - 1e-9) { result -= s; }
    result = Math.round((result + Number.EPSILON) * 100) / 100;
    if (result < 0) { result = 0; }
    request.amount = result;
  }

  // Clamp the edited payout so it never exceeds the requested value or drops <= 0.
  clampAmount(request: any) {
    let v = parseFloat(request.amount);
    if (isNaN(v) || v < 0) { v = 0; }
    if (v > request.max_amount) { v = request.max_amount; }
    request.amount = v;
  }

  changeBank(request: any) {
    const bank = this.banks.find((b: any) => b.id == request.bank_id);
    request.is_auto = bank ? bank.auto : false;
  }

  async uploadFile(event: any, request: any) {
    request.file = await this.http.fileChangeEvent(event);
  }

  async updateRequest(request: any) {
    // Multi-click guard: the approve POST is slow, so a repeatedly-clicked UPDATE
    // would fire multiple /admin/agent-withdraw/update calls (same duplicate-row
    // class of bug as the player withdraw). Block re-entry while one is pending.
    if (request._submitting) {
      return;
    }
    if (request.action_status === 1 && request.payout_type === 'bank' && !request.bank_id) {
      this.http.toast("Please select a bank to pay from.", "error");
      return;
    }
    if (request.action_status === 0 && !request.remark) {
      this.http.toast("Please enter a reason for rejection.", "error");
      return;
    }

    if (this.allow_edit_balance && request.action_status === 1) {
      let amount = parseFloat(request.amount);
      if (isNaN(amount) || amount <= 0) {
        this.http.toast("Payout amount must be greater than 0.", "error");
        return;
      }
      if (amount > request.max_amount) {
        this.http.toast("Payout amount cannot exceed the requested amount.", "error");
        return;
      }
    }

    request._submitting = true;
    let rtn: any;
    try {
      rtn = await this.http.post("/admin/agent-withdraw/update", {
        uuid: request.uuid,
        status: request.action_status,
        bank_id: request.bank_id,
        payout_type: request.payout_type,
        amount: request.amount,
        file: request.file,
        remark: request.remark
      });
    } finally {
      request._submitting = false;
    }

    if (rtn && rtn.status) {
      this.getRequests(this.status);
    }
    if (rtn && rtn.message) {
      this.http.toast(rtn.message, rtn.status ? "success" : "error");
    }
  }
}
